import {
  deltaE2000,
  IDENTITY_3,
  labToSrgb,
  linearToSrgb,
  srgbToLab,
  srgbToLinear,
  type Lab,
  type LinearRGB,
  type Matrix3,
  type SRGB,
} from '../color/index.js';
import { leastSquares } from './solve.js';
import type {
  Calibration,
  CalibrationModel,
  PatchMeasurement,
  PatchResidual,
  ReferenceCard,
} from './types.js';

/** Minimum patch count each model can be fitted from. */
export const MINIMUM_PATCHES: Record<CalibrationModel, number> = {
  identity: 0,
  diagonal: 1,
  linear3x3: 3,
};

/**
 * Minimum chromatic spread, in Lab (a,b) units, before a 3x3 fit is trustworthy.
 *
 * Patch count is not a sufficient test. Three *near-neutral* patches satisfy
 * `MINIMUM_PATCHES.linear3x3` and produce a non-singular system, but they sit
 * almost on the RGB grey axis, so the system is ill-conditioned: it reproduces
 * its own patches perfectly while amplifying sampling noise into wild
 * extrapolation off-axis.
 *
 * Measured with ~1.5 code values of noise on a set of three near-neutral paint
 * swatches (spread 6.8), a 3x3 fit gave a median error of 46 dE00 on a
 * saturated held-out colour, with a 95th percentile of 120 — far worse than no
 * correction at all. Adding one chromatic patch brought that to 2.1 median /
 * 7.9 p95. A diagonal fit on the same three neutrals was stable at 11 dE00.
 *
 * 20 sits well clear of both cases. It is an empirical threshold, not a
 * theoretical one; revisit it if the residuals suggest otherwise.
 */
export const MINIMUM_CHROMA_SPREAD = 20;

export class CalibrationError extends Error {}

/**
 * Largest distance between any two patches in the Lab (a,b) plane.
 *
 * Deliberately ignores L: lightness spread does nothing to condition the
 * off-diagonal terms of a 3x3 fit, which is exactly what goes wrong with an
 * all-neutral card.
 */
export function chromaticSpread(card: ReferenceCard): number {
  let max = 0;
  for (let i = 0; i < card.patches.length; i++) {
    for (let j = i + 1; j < card.patches.length; j++) {
      const p = card.patches[i] as { lab: Lab };
      const q = card.patches[j] as { lab: Lab };
      max = Math.max(max, Math.hypot(p.lab.a - q.lab.a, p.lab.b - q.lab.b));
    }
  }
  return max;
}

export interface ModelRecommendation {
  model: CalibrationModel;
  reason: string;
  chromaticSpread: number;
}

/**
 * Pick the most capable model the card can actually support.
 *
 * Prefer this to choosing by patch count. Returning `diagonal` for an
 * all-neutral card is not a limitation being worked around — it is the correct
 * answer, because the data cannot constrain anything richer.
 */
export function recommendModel(card: ReferenceCard): ModelRecommendation {
  const spread = chromaticSpread(card);
  const count = card.patches.length;

  if (count === 0) {
    return { model: 'identity', reason: 'No reference patches captured.', chromaticSpread: spread };
  }
  if (count < MINIMUM_PATCHES.linear3x3) {
    return {
      model: 'diagonal',
      reason: `Only ${count} patch(es); a 3x3 fit needs at least ${MINIMUM_PATCHES.linear3x3}.`,
      chromaticSpread: spread,
    };
  }
  if (spread < MINIMUM_CHROMA_SPREAD) {
    return {
      model: 'diagonal',
      reason:
        `Patches span only ${spread.toFixed(1)} Lab chroma units (need ${MINIMUM_CHROMA_SPREAD}). ` +
        'They are too close to neutral for a stable 3x3 fit. Add a saturated patch to the card ' +
        'to correct channel cross-talk as well as white balance.',
      chromaticSpread: spread,
    };
  }
  return {
    model: 'linear3x3',
    reason: `${count} patches spanning ${spread.toFixed(1)} chroma units.`,
    chromaticSpread: spread,
  };
}

/**
 * Fit a correction that maps measured patch colours onto the card's targets.
 *
 * Both sides are converted to linear light before fitting, because that is the
 * space in which illumination behaves multiplicatively. The returned
 * `provenance` is inherited from the card, so an absolute claim can only ever
 * originate from an absolute card.
 */
export function fitCalibration(
  card: ReferenceCard,
  measurements: PatchMeasurement[],
  model: CalibrationModel = 'linear3x3',
): Calibration {
  const patchesById = new Map(card.patches.map((p) => [p.id, p]));

  const pairs: Array<{ patchId: string; name: string; measured: LinearRGB; target: LinearRGB; targetLab: Lab }> = [];
  for (const measurement of measurements) {
    const patch = patchesById.get(measurement.patchId);
    if (!patch) {
      throw new CalibrationError(
        `Measurement references patch "${measurement.patchId}", which is not on card "${card.id}".`,
      );
    }
    pairs.push({
      patchId: patch.id,
      name: patch.name,
      measured: srgbToLinear(measurement.srgb),
      target: srgbToLinear(labToSrgb(patch.lab, card.illuminant)),
      targetLab: patch.lab,
    });
  }

  const required = MINIMUM_PATCHES[model];
  if (pairs.length < required) {
    throw new CalibrationError(
      `Model "${model}" needs at least ${required} measured patch(es); got ${pairs.length}.`,
    );
  }

  let matrix: Matrix3;
  switch (model) {
    case 'identity':
      matrix = IDENTITY_3;
      break;
    case 'diagonal':
      matrix = fitDiagonal(pairs);
      break;
    case 'linear3x3':
      matrix = fitLinear3x3(pairs);
      break;
  }

  const residuals: PatchResidual[] = pairs.map((pair) => {
    const corrected = applyMatrixToLinear(matrix, pair.measured);
    const correctedLab = srgbToLab(linearToSrgb(corrected), card.illuminant);
    return {
      patchId: pair.patchId,
      name: pair.name,
      deltaE: deltaE2000(correctedLab, pair.targetLab),
    };
  });

  const deltas = residuals.map((r) => r.deltaE);

  return {
    model,
    matrix,
    cardId: card.id,
    provenance: card.provenance,
    illuminant: card.illuminant,
    residuals,
    meanDeltaE: deltas.length === 0 ? 0 : deltas.reduce((a, b) => a + b, 0) / deltas.length,
    maxDeltaE: deltas.length === 0 ? 0 : Math.max(...deltas),
  };
}

/** Per-channel least-squares gain. */
function fitDiagonal(pairs: Array<{ measured: LinearRGB; target: LinearRGB }>): Matrix3 {
  const gain = (channel: 'r' | 'g' | 'b'): number => {
    let numerator = 0;
    let denominator = 0;
    for (const { measured, target } of pairs) {
      numerator += measured[channel] * target[channel];
      denominator += measured[channel] * measured[channel];
    }
    // A channel that read pure zero across every patch carries no information;
    // leaving its gain at 1 is the honest identity choice.
    return denominator < 1e-12 ? 1 : numerator / denominator;
  };

  return [gain('r'), 0, 0, 0, gain('g'), 0, 0, 0, gain('b')];
}

/** Full 3x3 least squares: one independent 3-term fit per output channel. */
function fitLinear3x3(pairs: Array<{ measured: LinearRGB; target: LinearRGB }>): Matrix3 {
  const rows = pairs.map(({ measured }) => [measured.r, measured.g, measured.b]);
  const out: number[] = [];

  for (const channel of ['r', 'g', 'b'] as const) {
    const targets = pairs.map(({ target }) => target[channel]);
    const solution = leastSquares(rows, targets);
    if (!solution) {
      throw new CalibrationError(
        'Could not fit a 3x3 correction: the measured patches are collinear or degenerate. ' +
          'Use patches that differ in hue as well as lightness, or fall back to the "diagonal" model.',
      );
    }
    out.push(...solution);
  }

  return out as unknown as Matrix3;
}

function applyMatrixToLinear(m: Matrix3, c: LinearRGB): LinearRGB {
  return {
    r: m[0] * c.r + m[1] * c.g + m[2] * c.b,
    g: m[3] * c.r + m[4] * c.g + m[5] * c.b,
    b: m[6] * c.r + m[7] * c.g + m[8] * c.b,
  };
}

/** Apply a fitted calibration to a raw sRGB sample. */
export function applyCalibration(calibration: Calibration, srgb: SRGB): SRGB {
  return linearToSrgb(applyMatrixToLinear(calibration.matrix, srgbToLinear(srgb)));
}

/** A calibrated reading, with the raw value retained for audit. */
export interface Measurement {
  raw: { srgb: SRGB; lab: Lab };
  calibrated: { srgb: SRGB; lab: Lab };
  provenance: Calibration['provenance'];
  /** Closest patch on the card the calibration was fitted against, if any. */
  nearest?: { patchId: string; name: string; deltaE: number };
}

/**
 * Convert a raw sample into a calibrated measurement.
 *
 * `card` is optional and only used to report the nearest known patch; pass the
 * same card the calibration was fitted against.
 */
export function measure(
  calibration: Calibration,
  srgb: SRGB,
  card?: ReferenceCard,
): Measurement {
  const calibratedSrgb = applyCalibration(calibration, srgb);
  const calibratedLab = srgbToLab(calibratedSrgb, calibration.illuminant);

  const result: Measurement = {
    raw: { srgb, lab: srgbToLab(srgb, calibration.illuminant) },
    calibrated: { srgb: calibratedSrgb, lab: calibratedLab },
    provenance: calibration.provenance,
  };

  if (card && card.patches.length > 0) {
    let best = { patchId: '', name: '', deltaE: Number.POSITIVE_INFINITY };
    for (const patch of card.patches) {
      const deltaE = deltaE2000(calibratedLab, patch.lab);
      if (deltaE < best.deltaE) best = { patchId: patch.id, name: patch.name, deltaE };
    }
    result.nearest = best;
  }

  return result;
}
